import { getCreditStatus } from './billing'
import { listMonthlyBookings } from './bookings'
import { listContacts } from './contacts'
import { listConversations } from './conversations'
import { getReports } from './tenant'

const DEFAULT_UPCOMING_MINUTES = 60
const DEFAULT_CACHE_TTL_MS = 30 * 1000
const dashboardCache = new Map()
const dashboardRequests = new Map()

function getCacheKey(tenantId, options) {
  return JSON.stringify({
    tenantId,
    timePreset: options.timePreset || 'today',
    source: options.source || 'all',
    upcomingMinutes: options.upcomingMinutes || DEFAULT_UPCOMING_MINUTES,
    date: options.date || null,
    rangeStart: options.rangeStart || null,
    rangeEnd: options.rangeEnd || null,
  })
}

function parseDateTime(value) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function dateKey(date = new Date()) {
  const d = parseDateTime(date) || new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function currentMonthKey(date = new Date()) {
  const d = parseDateTime(date) || new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthKeysBetween(start, end) {
  const from = startOfLocalDay(start)
  const to = startOfLocalDay(end)
  const months = []
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1)
  const last = new Date(to.getFullYear(), to.getMonth(), 1)

  while (cursor <= last) {
    months.push(currentMonthKey(cursor))
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return months
}

function startOfLocalDay(date = new Date()) {
  const d = parseDateTime(date) || new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function endOfLocalDay(date = new Date()) {
  const start = startOfLocalDay(date)
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1)
}

function isWithinRange(value, start, end) {
  const date = parseDateTime(value)
  if (!date) return false
  return date >= start && date <= end
}

function getRangeForPreset(preset, targetDate = new Date()) {
  const target = parseDateTime(targetDate) || new Date()
  const todayStart = startOfLocalDay(target)
  const todayEnd = endOfLocalDay(target)

  if (preset === 'yesterday') {
    const yesterday = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000)
    return { start: startOfLocalDay(yesterday), end: endOfLocalDay(yesterday) }
  }

  if (preset === 'last7Days') {
    return {
      start: new Date(todayStart.getTime() - 6 * 24 * 60 * 60 * 1000),
      end: todayEnd,
    }
  }

  if (preset === 'thisMonth') {
    return {
      start: new Date(target.getFullYear(), target.getMonth(), 1),
      end: todayEnd,
    }
  }

  return { start: todayStart, end: todayEnd }
}

function getPreviousRange(start, end) {
  const duration = end.getTime() - start.getTime() + 1
  const previousEnd = new Date(start.getTime() - 1)
  const previousStart = new Date(previousEnd.getTime() - duration + 1)
  return { start: previousStart, end: previousEnd }
}

function normalizeBookingStatus(status) {
  if (status === 'awaiting_deposit') return 'deposit_pending'
  if (status === 'awaiting_confirmation') return 'pending'
  return status || 'pending'
}

function normalizeTimeSlot(slot) {
  if (!slot || typeof slot !== 'string') return '19:00'
  return slot.slice(0, 5)
}

function normalizeSource(source) {
  const raw = String(source || '').trim().toLowerCase()
  if (!raw) return 'unknown'
  if (raw === 'widget') return 'web'
  if (raw === 'messenger') return 'facebook'
  return raw
}

function matchesSource(value, source) {
  const selected = normalizeSource(source)
  if (!selected || selected === 'all' || selected === 'unknown') return true
  return normalizeSource(value) === selected
}

function bookingDateTime(booking) {
  const date = booking.date || booking.booking_date || booking.start_date
  const time = normalizeTimeSlot(booking.time_slot || booking.time || booking.start_time)
  return parseDateTime(`${date}T${time}:00`)
}

function normalizeBooking(rawBooking) {
  return {
    ...rawBooking,
    status: normalizeBookingStatus(rawBooking?.status),
    date: rawBooking?.date || rawBooking?.booking_date || rawBooking?.start_date || '',
    time_slot: normalizeTimeSlot(rawBooking?.time_slot || rawBooking?.time || rawBooking?.start_time),
    guests: Number(rawBooking?.party_size ?? rawBooking?.guests ?? rawBooking?.guest_count ?? 1) || 1,
    source: normalizeSource(rawBooking?.channel || rawBooking?.source),
  }
}

function getConversationSource(conversation) {
  return conversation?.channel || conversation?.source || conversation?.platform
}

function getContactSources(contact) {
  const channels = Array.isArray(contact?.channels) ? contact.channels : []
  const channelSources = channels.map(channel =>
    channel?.platform || channel?.channel || channel?.source || channel
  )
  return [
    contact?.source,
    contact?.channel,
    contact?.platform,
    ...channelSources,
  ].filter(Boolean)
}

function extractRows(payload, keys = ['items', 'bookings', 'conversations', 'data']) {
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

function getMessageTimestamp(message, fallback) {
  return (
    message?.created_at ||
    message?.createdAt ||
    message?.timestamp ||
    message?.sent_at ||
    message?.updated_at ||
    fallback
  )
}

function countMessagesForRange(conversations, start, end, source) {
  return conversations.reduce((total, conversation) => {
    if (!matchesSource(getConversationSource(conversation), source)) return total

    const messages = Array.isArray(conversation?.messages) ? conversation.messages : []
    if (!messages.length) {
      return isWithinRange(conversation?.updated_at || conversation?.created_at, start, end)
        ? total + (Number(conversation?.message_count) || 0)
        : total
    }

    return total + messages.filter(message =>
      isWithinRange(getMessageTimestamp(message, conversation?.updated_at), start, end)
    ).length
  }, 0)
}

function countContactsForRange(contacts, start, end, source) {
  return contacts.filter(contact =>
    isWithinRange(contact?.created_at || contact?.createdAt || contact?.inserted_at, start, end) &&
    (source === 'all' || getContactSources(contact).some(contactSource => matchesSource(contactSource, source)))
  ).length
}

function buildBookingSummary(bookings, start, end, upcomingMinutes, source) {
  const now = new Date()
  const upcomingEnd = new Date(now.getTime() + upcomingMinutes * 60 * 1000)
  const sourceBookings = bookings.filter(booking => matchesSource(booking.source, source))
  const periodBookings = sourceBookings.filter(booking => {
    const bookingDate = bookingDateTime(booking)
    return bookingDate ? bookingDate >= start && bookingDate <= end : booking.date === dateKey(start)
  })
  const activePeriodBookings = periodBookings.filter(booking => booking.status !== 'cancelled')
  const upcomingBookings = sourceBookings.filter(booking => {
    const bookingDate = bookingDateTime(booking)
    return (
      bookingDate &&
      bookingDate >= now &&
      bookingDate <= upcomingEnd &&
      ['confirmed', 'completed'].includes(booking.status)
    )
  })

  return {
    totalBookings: activePeriodBookings.length,
    upcomingGuests: upcomingBookings.reduce((sum, booking) => sum + booking.guests, 0),
    pendingBookings: periodBookings.filter(booking =>
      ['pending', 'deposit_pending'].includes(booking.status)
    ).length,
    confirmedBookings: periodBookings.filter(booking => booking.status === 'confirmed').length,
  }
}

function fallbackNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function trendFor(current, previous) {
  const currentValue = fallbackNumber(current)
  const previousValue = fallbackNumber(previous)
  const delta = currentValue - previousValue
  const pct = previousValue === 0
    ? (currentValue > 0 ? 100 : 0)
    : Math.round((delta / previousValue) * 100)

  return {
    current: currentValue,
    previous: previousValue,
    delta,
    pct,
  }
}

function incrementSource(map, source, amount = 1) {
  const key = normalizeSource(source)
  if (!key || key === 'unknown') return
  map.set(key, (map.get(key) || 0) + amount)
}

function getBusiestSource(conversations, contacts, bookings, start, end) {
  const sourceCounts = new Map()

  conversations.forEach(conversation => {
    const source = getConversationSource(conversation)
    const messages = Array.isArray(conversation?.messages) ? conversation.messages : []
    if (messages.length) {
      const count = messages.filter(message =>
        isWithinRange(getMessageTimestamp(message, conversation?.updated_at), start, end)
      ).length
      incrementSource(sourceCounts, source, count)
      return
    }
    if (isWithinRange(conversation?.updated_at || conversation?.created_at, start, end)) {
      incrementSource(sourceCounts, source, Number(conversation?.message_count) || 1)
    }
  })

  contacts.forEach(contact => {
    if (!isWithinRange(contact?.created_at || contact?.createdAt || contact?.inserted_at, start, end)) return
    getContactSources(contact).forEach(source => incrementSource(sourceCounts, source))
  })

  bookings.forEach(booking => {
    const date = bookingDateTime(booking)
    if (date && date >= start && date <= end) incrementSource(sourceCounts, booking.source)
  })

  const [source, count] = [...sourceCounts.entries()].sort((a, b) => b[1] - a[1])[0] || []
  return source ? { source, count } : null
}

function countUnreadConversations(conversations, source) {
  return conversations.filter(conversation =>
    matchesSource(getConversationSource(conversation), source) &&
    fallbackNumber(conversation?.unread_count ?? conversation?.unread) > 0
  ).length
}

function countStaffModeConversations(conversations, source) {
  return conversations.filter(conversation =>
    matchesSource(getConversationSource(conversation), source) &&
    conversation?.ai_enabled === false
  ).length
}

function buildMetrics(conversations, contacts, bookings, reports, start, end, upcomingMinutes, source) {
  const totalMessages = countMessagesForRange(conversations, start, end, source)
  const newContacts = countContactsForRange(contacts, start, end, source)
  const bookingSummary = buildBookingSummary(bookings, start, end, upcomingMinutes, source)

  return {
    totalMessages: totalMessages || fallbackNumber(reports?.messages ?? reports?.conversations),
    newContacts: newContacts || fallbackNumber(reports?.new_contacts ?? reports?.leads),
    ...bookingSummary,
  }
}

async function loadDashboardSummary(tenantId, options = {}) {
  if (!tenantId) return null

  const targetDate = parseDateTime(options.date) || new Date()
  const presetRange = getRangeForPreset(options.timePreset, targetDate)
  const rangeStart = (options.rangeStart ? parseDateTime(options.rangeStart) : presetRange.start) || presetRange.start
  const rangeEnd = (options.rangeEnd ? parseDateTime(options.rangeEnd) : presetRange.end) || presetRange.end
  const previousRange = getPreviousRange(rangeStart, rangeEnd)
  const upcomingMinutes = Number(options.upcomingMinutes) || DEFAULT_UPCOMING_MINUTES
  const source = normalizeSource(options.source || 'all')
  const upcomingEnd = new Date(Date.now() + upcomingMinutes * 60 * 1000)
  const bookingMonths = Array.from(new Set([
    ...monthKeysBetween(rangeStart, rangeEnd),
    ...monthKeysBetween(new Date(), upcomingEnd),
  ]))

  const [
    creditResult,
    reportsResult,
    conversationsResult,
    contactsResult,
    ...bookingResults
  ] = await Promise.allSettled([
    getCreditStatus(tenantId),
    getReports(tenantId),
    listConversations(tenantId),
    listContacts(tenantId),
    ...bookingMonths.map(month => listMonthlyBookings(tenantId, month)),
  ])

  const reports = reportsResult.status === 'fulfilled' ? reportsResult.value : null
  const conversations = conversationsResult.status === 'fulfilled'
    ? extractRows(conversationsResult.value, ['items', 'conversations', 'data'])
    : []
  const contacts = contactsResult.status === 'fulfilled'
    ? extractRows(contactsResult.value, ['items', 'contacts', 'data'])
    : []
  const bookings = bookingResults.flatMap(result =>
    result.status === 'fulfilled'
      ? extractRows(result.value, ['bookings', 'items', 'data']).map(normalizeBooking)
      : []
  )

  const metrics = buildMetrics(conversations, contacts, bookings, reports, rangeStart, rangeEnd, upcomingMinutes, source)
  const previousMetrics = buildMetrics(
    conversations,
    contacts,
    bookings,
    null,
    previousRange.start,
    previousRange.end,
    upcomingMinutes,
    source
  )
  const conversionRate = metrics.totalBookings > 0
    ? Math.round((metrics.confirmedBookings / metrics.totalBookings) * 100)
    : 0

  return {
    creditStatus: creditResult.status === 'fulfilled' ? creditResult.value : null,
    reports,
    metrics,
    trends: {
      totalMessages: trendFor(metrics.totalMessages, previousMetrics.totalMessages),
      newContacts: trendFor(metrics.newContacts, previousMetrics.newContacts),
      totalBookings: trendFor(metrics.totalBookings, previousMetrics.totalBookings),
      pendingBookings: trendFor(metrics.pendingBookings, previousMetrics.pendingBookings),
      confirmedBookings: trendFor(metrics.confirmedBookings, previousMetrics.confirmedBookings),
    },
    insights: {
      busiestSource: getBusiestSource(conversations, contacts, bookings, rangeStart, rangeEnd),
      unreadConversations: countUnreadConversations(conversations, source),
      staffModeConversations: countStaffModeConversations(conversations, source),
      bookingConversionRate: conversionRate,
    },
    meta: {
      lastUpdatedAt: new Date().toISOString(),
      rangeStart: rangeStart.toISOString(),
      rangeEnd: rangeEnd.toISOString(),
      previousRangeStart: previousRange.start.toISOString(),
      previousRangeEnd: previousRange.end.toISOString(),
      upcomingMinutes,
      source,
      partial: [
        reportsResult,
        conversationsResult,
        contactsResult,
        ...bookingResults,
      ].some(result => result.status === 'rejected'),
    },
  }
}

export async function getDashboardSummary(tenantId, options = {}) {
  if (!tenantId) return null

  const cacheKey = getCacheKey(tenantId, options)
  const cacheTtlMs = Number(options.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS)
  const cached = dashboardCache.get(cacheKey)
  if (!options.force && cached && Date.now() - cached.createdAt <= cacheTtlMs) {
    return cached.data
  }

  if (!options.force && dashboardRequests.has(cacheKey)) {
    return dashboardRequests.get(cacheKey)
  }

  const request = loadDashboardSummary(tenantId, options)
    .then(data => {
      dashboardCache.set(cacheKey, { createdAt: Date.now(), data })
      return data
    })
    .finally(() => {
      dashboardRequests.delete(cacheKey)
    })

  dashboardRequests.set(cacheKey, request)
  return request
}
